/**
 * A button that changes something, at one of three strengths (admin-design-r1):
 * - `none`: safe, runs on the click (Restart Paseo).
 * - `confirm`: says what stops and for how long, then Yes or Cancel (Reboot).
 * - `type`: the person types the slug before Yes unlocks (Teardown).
 * Whoever presses it is written to the audit by the API; the question should say so.
 */
import { useId, useState, type ReactNode } from 'react'
import { Button } from '../primitives/Button'
import { Input } from '../primitives/Input'
import s from './ConfirmAction.module.css'

export function ConfirmAction({
  label,
  strength = 'none',
  question,
  yes,
  slug,
  onConfirm,
  disabled,
  reason,
  working,
  variant = 'soft',
  children,
}: {
  label: string
  strength?: 'none' | 'confirm' | 'type'
  /** What happens, in a sentence or two. Required for confirm and type. */
  question?: ReactNode
  /** The confirm button's words; defaults to the label. */
  yes?: string
  /** What to type for `type`. */
  slug?: string
  onConfirm: () => void
  disabled?: boolean
  /** Why it's greyed out, beside the button. */
  reason?: string | null
  working?: boolean
  variant?: 'soft' | 'ink' | 'ghost' | 'line'
  /** What happened, after it ran. */
  children?: ReactNode
}) {
  const [asking, setAsking] = useState(false)
  const [typed, setTyped] = useState('')
  const inputId = useId()

  if (working) {
    return (
      <div className={s.act} role="status">
        <span className={s.spin} aria-hidden="true" />
        <span>{label}: working…</span>
      </div>
    )
  }
  if (asking && strength !== 'none') {
    const ready = strength === 'confirm' || typed.trim() === slug
    const done = () => (setAsking(false), setTyped(''))
    return (
      <div className={s.ask} role="group" aria-label={`Confirm: ${label}`}>
        <div className={s.q}>{question}</div>
        {strength === 'type' && (
          <label className={s.typeRow} htmlFor={inputId}>
            <span>
              Type <b className={s.slug}>{slug}</b> to confirm.
            </span>
            <Input id={inputId} mono value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
          </label>
        )}
        <div className={s.row}>
          <Button variant={strength === 'type' ? 'danger' : 'ink'} size="sm" disabled={!ready} onClick={() => (done(), onConfirm())}>
            {yes ?? label}
          </Button>
          <Button variant="ghost" size="sm" onClick={done} autoFocus={strength === 'confirm'}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }
  return (
    <div className={s.act}>
      <Button variant={variant} size="sm" disabled={disabled} onClick={() => (strength === 'none' ? onConfirm() : setAsking(true))}>
        {label}
      </Button>
      {disabled && reason && <span className={s.why}>{reason}</span>}
      {children}
    </div>
  )
}
